import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form'
import { Icon } from '@/common/components/icon'
import * as styles from './Checkbox.css'
import type { RootVariants } from './Checkbox.css'

// =============================================================================
// Types
// =============================================================================

type CheckboxBaseProps = Omit<CheckboxPrimitive.CheckboxProps, 'children' | 'checked' | 'onCheckedChange'> &
  NonNullable<RootVariants> & {
    /** Label content to display next to checkbox (string or rich content) */
    label?: React.ReactNode
  }

// Props for standalone usage (no react-hook-form)
type CheckboxStandaloneProps = CheckboxBaseProps & {
  /** Current checked state */
  checked?: boolean | 'indeterminate'
  /** Called when checked state changes */
  onCheckedChange?: (checked: boolean | 'indeterminate') => void
}

// Props for react-hook-form usage
type CheckboxRHFProps<T extends FieldValues, N extends FieldPath<T>> = CheckboxBaseProps & {
  /** Field name for react-hook-form */
  name: N
  /** Control object from useForm */
  control: Control<T>
}

export type CheckboxProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> =
  | CheckboxStandaloneProps
  | CheckboxRHFProps<T, N>

// Type guard for RHF props
function isRHFProps<T extends FieldValues, N extends FieldPath<T>>(
  props: CheckboxProps<T, N>
): props is CheckboxRHFProps<T, N> {
  return 'name' in props && 'control' in props
}

// =============================================================================
// Component
// =============================================================================

function CheckboxInner<T extends FieldValues, N extends FieldPath<T>>(
  props: CheckboxProps<T, N>,
  ref: React.ForwardedRef<React.ComponentRef<typeof CheckboxPrimitive.Root>>
) {
  const { label, size, error: errorProp, className, disabled } = props

  // RHF integration
  let checked: boolean | 'indeterminate' | undefined
  let onCheckedChange: ((checked: boolean | 'indeterminate') => void) | undefined
  let fieldError = false
  let otherProps: Omit<CheckboxBaseProps, 'label' | 'size' | 'error' | 'className' | 'disabled'>

  if (isRHFProps(props)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { name, control, label: _l, size: _s, error: _e, className: _c, disabled: _d, ...rest } = props
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field, fieldState } = useController({ name, control })
    checked = field.value === true ? true : field.value === 'indeterminate' ? 'indeterminate' : false
    onCheckedChange = (value) => field.onChange(value === true ? true : value === 'indeterminate' ? 'indeterminate' : false)
    fieldError = !!fieldState.error
    otherProps = rest
  } else {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { checked: c, onCheckedChange: oc, label: _l, size: _s, error: _e, className: _c, disabled: _d, ...rest } = props as CheckboxStandaloneProps
    checked = c
    onCheckedChange = oc
    otherProps = rest
  }

  const hasError = errorProp ?? fieldError

  const checkbox = (
    <CheckboxPrimitive.Root
      ref={ref}
      className={`${styles.root({ size, error: hasError })} ${className ?? ''}`}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      {...otherProps}
    >
      <CheckboxPrimitive.Indicator className={styles.indicator}>
        {checked === 'indeterminate' ? (
          <Icon name="remove" size={size === 'sm' ? 14 : size === 'lg' ? 18 : 16} />
        ) : (
          <Icon name="check" size={size === 'sm' ? 14 : size === 'lg' ? 18 : 16} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )

  if (label) {
    return (
      <label className={styles.wrapper}>
        {checkbox}
        <span className={styles.labelText} data-disabled={disabled ? '' : undefined}>
          {label}
        </span>
      </label>
    )
  }

  return checkbox
}

// ForwardRef wrapper with generic support
const Checkbox = React.forwardRef(CheckboxInner) as <
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>
>(
  props: CheckboxProps<T, N> & { ref?: React.ForwardedRef<React.ComponentRef<typeof CheckboxPrimitive.Root>> }
) => React.ReactElement

;(Checkbox as React.FC).displayName = 'Checkbox'

export { Checkbox }
